/**
 * GoldSweep.js
 * Replaces the old Three.js Particles system.
 * Pure Canvas 2D overlay — zero Three.js dependency, runs on its own loop.
 * Keeps the same public interface (models, smokeAnimation, tick) so
 * threeSetup.js and script.js need minimal changes.
 */
export default class GoldSweep {
    constructor() {
        // Empty array — no Three.js objects.
        // script.js does models.forEach(visible=true/false) which becomes a no-op.
        this.models = [];

        this._canvas    = null;
        this._ctx       = null;
        this._animating = false;
        this._particles = [];
        this._raf       = null;

        this._setupCanvas();
    }

    // ── Canvas overlay setup ─────────────────────────────────────────────
    _setupCanvas() {
        this._canvas = document.createElement('canvas');
        this._canvas.style.cssText = [
            'position: fixed',
            'top: 0',
            'left: 0',
            'width: 100%',
            'height: 100%',
            'pointer-events: none',   // never blocks clicks
            'z-index: 9998',          // below popups, above everything else
        ].join(';');
        document.body.appendChild(this._canvas);
        this._ctx = this._canvas.getContext('2d');
        this._fit();
        window.addEventListener('resize', () => this._fit(), { passive: true });
    }

    _fit() {
        const dpr = Math.min(window.devicePixelRatio, 2);
        this._canvas.width  = window.innerWidth  * dpr;
        this._canvas.height = window.innerHeight * dpr;
    }

    // ── Particle factory ─────────────────────────────────────────────────
    _makePart() {
        const h = this._canvas.height;
        const spd = 3.8 + Math.random() * 3.8;
        return {
            x:    -14 - Math.random() * 45,
            // distribute across full height with slight overflow top/bottom
            y:    -h * 0.12 + Math.random() * h * 1.24,
            vx:   spd,
            // two independent sine waves = harmonic but erratic
            amp1: 20  + Math.random() * 48,
            frq1: 0.016 + Math.random() * 0.03,
            ph1:  Math.random() * Math.PI * 2,
            amp2: 6   + Math.random() * 20,
            frq2: 0.038 + Math.random() * 0.058,
            ph2:  Math.random() * Math.PI * 2,
            // slow vertical drift that curls the tendril
            drift:      (Math.random() - 0.5) * 0.2,
            driftPhase: Math.random() * Math.PI * 2,
            // trail
            trail:    [],
            trailLen: 18 + Math.floor(Math.random() * 24),
            // visuals — warm gold, never garish
            r:     0.9 + Math.random() * 2.5,
            alpha: 0.32 + Math.random() * 0.52,
            hue:   34  + Math.random() * 20,
            sat:   52  + Math.random() * 28,
            lit:   58  + Math.random() * 26,
            t:     0,
            delay: 0,
        };
    }

    // ── Public: called by threeSetup.js smokeAnimation() ────────────────
    /**
     * @param {Function} [onMidpoint] - optional callback fired at sweep midpoint
     *                                  (use to swap section content)
     */
    smokeAnimation(onMidpoint) {
        // prevent double-firing
        if (this._animating) return;
        this._animating = true;
        this._particles = [];
        this._fit();

        const count    = window.innerWidth < 768 ? 40 : 60;
        const midFrame = 38; // ~0.63s at 60fps — midpoint of the sweep
        let   frame    = 0;
        let   midFired = false;

        // stagger spawn so particles fan out like a wave, not a wall
        for (let i = 0; i < count; i++) {
            const p  = this._makePart();
            p.delay  = (i / count) * 24 + Math.random() * 8;
            this._particles.push(p);
        }

        const step = () => {
            const cv  = this._canvas;
            const ctx = this._ctx;
            ctx.clearRect(0, 0, cv.width, cv.height);

            let allDone = true;

            this._particles.forEach(p => {
                if (p.delay > 0) { p.delay--; allDone = false; return; }

                p.t++;
                p.x += p.vx;

                // y-delta from harmonic oscillation (derivative approach keeps it smooth)
                const prev = Math.sin((p.t-1)*p.frq1+p.ph1)*p.amp1
                           + Math.sin((p.t-1)*p.frq2+p.ph2)*p.amp2
                           + Math.sin((p.t-1)*0.008+p.driftPhase)*14;
                const curr = Math.sin(p.t*p.frq1+p.ph1)*p.amp1
                           + Math.sin(p.t*p.frq2+p.ph2)*p.amp2
                           + Math.sin(p.t*0.008+p.driftPhase)*14;
                p.y += p.drift + (curr - prev);

                p.trail.push({ x: p.x, y: p.y });
                if (p.trail.length > p.trailLen) p.trail.shift();

                const offRight = p.x > cv.width + 24;
                if (!offRight) allDone = false;

                // draw fading trail
                if (p.trail.length > 1) {
                    for (let i = 1; i < p.trail.length; i++) {
                        const prog = i / p.trail.length;
                        const a    = p.alpha * prog
                                   * (offRight
                                       ? Math.max(0, 1 - (p.x - cv.width) / 70)
                                       : 1);
                        if (a <= 0.01) continue;
                        ctx.beginPath();
                        ctx.moveTo(p.trail[i-1].x, p.trail[i-1].y);
                        ctx.lineTo(p.trail[i].x,   p.trail[i].y);
                        ctx.strokeStyle = `hsl(${p.hue},${p.sat}%,${p.lit}%)`;
                        ctx.lineWidth   = Math.max(0.3, p.r * prog);
                        ctx.globalAlpha = a;
                        ctx.stroke();
                    }
                }

                // bright head dot
                if (!offRight && p.trail.length) {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                    ctx.fillStyle   = `hsl(${p.hue},${p.sat+10}%,${Math.min(p.lit+12, 90)}%)`;
                    ctx.globalAlpha = p.alpha;
                    ctx.fill();
                }

                ctx.globalAlpha = 1;
            });

            // fire midpoint callback — caller uses this to swap section
            frame++;
            if (frame === midFrame && !midFired) {
                midFired = true;
                if (typeof onMidpoint === 'function') onMidpoint();
            }

            if (!allDone) {
                this._raf = requestAnimationFrame(step);
            } else {
                ctx.clearRect(0, 0, cv.width, cv.height);
                this._animating = false;
            }
        };

        step();
    }

    // ── tick is called by threeSetup's tickElements every frame ─────────
    // Nothing to do here — Canvas 2D has its own RAF loop above.
    tick() {}
}